import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { Encouragement } from 'src/app/data/patient/domain/encouragement/encouragement';
import { EncouragementCommands } from 'src/app/data/patient/domain/encouragement/encouragement_commands';
import { Diary, DiaryProps } from 'src/app/data/tracker/domain/diary/diary';
import { DiaryCommands } from 'src/app/data/tracker/domain/diary/diary_commands';

import { LoadingStatus } from '../../../../core/domain/events/state_provider';
import { Dietitian } from '../../../../data/dietitian/domain/dietitian';
import { DietitianStateProvider } from '../../../../data/dietitian/domain/dietitian_state_provider';
import { MsdAiService } from '../../../../data/msd-ai/services/msd-ai.service';
import { Gender, Patient } from '../../../../data/patient/domain/patient';
import { PatientStateProvider } from '../../../../data/patient/domain/patient_state_provider';
import { PatientRepository } from '../../../../data/patient/repositories/patient_repository';
import { DiaryType } from '../../../../data/tracker/domain/diary_type/diary_type';
import { DiaryTypeCommands } from '../../../../data/tracker/domain/diary_type/diary_type_commands';

export interface PatientInfoViewModel {
  isLoading: boolean;
  error: string;
  patient: Patient;
}

export interface AccountViewModel {
  isLoading: boolean;
  error: string;
  dietitian: Dietitian;
}

@Injectable()
export class DiaryFacade {
  constructor(
    private patientRepository: PatientRepository,
    private diaryCommands: DiaryCommands,
    private diaryTypeCommands: DiaryTypeCommands,
    private encouragementCommands: EncouragementCommands,
    private dietitianStateProvider: DietitianStateProvider,
    private patientStateProvider: PatientStateProvider,
    private msdAiService: MsdAiService,
  ) {
    // do nothing
  }

  get accountStatus$(): Observable<AccountViewModel> {
    return this.dietitianStateProvider.state$.pipe(
      map((e) => {
        return <AccountViewModel>{
          isLoading: e.loading == LoadingStatus.LOADING,
          error: e.loading == LoadingStatus.ERROR ? e.message : undefined,
          dietitian: e.entity,
        };
      }),
    );
  }

  get patientStatus$(): Observable<PatientInfoViewModel> {
    return this.patientStateProvider.state$.pipe(
      map((e) => {
        return <PatientInfoViewModel>{
          isLoading: e.loading == LoadingStatus.LOADING,
          error: e.loading == LoadingStatus.ERROR ? e.message : undefined,
          patient: e.entity,
        };
      }),
    );
  }

  getPatientDiaries(
    patientId: string,
    start: Date,
    end: Date,
  ): Promise<Diary[]> {
    return this.diaryCommands.getPatientDiaries(patientId, start, end);
  }

  getPatientEncouragements(
    patientId: string,
    start: Date,
    end: Date,
  ): Promise<Encouragement[]> {
    return this.encouragementCommands.getPatientEncouragements(
      patientId,
      start,
      end,
    );
  }

  updateDiaryComment(diaryId: string, props: DiaryProps, comment?: string) {
    return this.diaryCommands.saveDiary(
      {
        ...props,
        comment,
      },
      diaryId,
    );
  }

  async findAllDiaryType(
    dietitianId: string | undefined,
    patientId: string | undefined,
  ): Promise<DiaryType[]> {
    return this.diaryTypeCommands.findAllDiaryTypes(
      dietitianId,
      undefined,
      patientId,
    );
  }

  generateDiaryPdf(
    patientId: string,
    date: Date,
    withEncouragements: boolean,
    withImages: boolean,
  ) {
    return this.diaryCommands.generateDiaryPdf(
      patientId,
      date,
      withEncouragements,
      withImages,
    );
  }

  async doMsdAi(
    dietitianId: string,
    patientId: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    diaries: Diary[],
  ): Promise<any> {
    const patient = await this.patientRepository.load(patientId);
    const genre =
      patient.gender === Gender.MAN
        ? 'un homme'
        : patient.gender === Gender.WOMAN
        ? 'une femme'
        : 'non binaire';
    const age = patient.age?.toString() ?? '30';
    const repas =
      "Un hamburger provenant d'un fast-food + un portion de frite + un coca zero";
    const moment = 'du midi';
    const result: any = await this.msdAiService.launchTestAI(
      dietitianId,
      genre,
      age,
      repas,
      moment,
    );
    return result;
  }
}
